import { Monitor, TrendingUp, Users, MessageSquare, Eye, Radio } from 'lucide-react';

// ─────────────────────────────────────────────────
// Value Pillars (Section 2)
// ─────────────────────────────────────────────────

export const pillars = [
  {
    icon: Eye,
    title: 'See the Delta.',
    description:
      'Pixel-perfect visual regression. Catch the moment a competitor rewrites their pricing page, launches a new feature tier, or A/B tests a CTA — before your client\'s board meeting.',
  },
  {
    icon: TrendingUp,
    title: 'Own the SERP.',
    description:
      "Go beyond rank tracking. Correlate keyword movements with GA4 traffic and Share of Voice to prove your agency's ROI — in dollars, not just positions.",
  },
  {
    icon: Radio,
    title: 'Signal Over Noise.',
    description:
      'Route the signals that matter — a competitor pricing pivot, a platform outage, a SERP crash — directly to the right team member via Slack, Asana, or email.',
  },
];

// ─────────────────────────────────────────────────
// Features (Section 4)
// ─────────────────────────────────────────────────

export const features = [
  {
    id: 'visual-regression',
    icon: Monitor,
    title: 'Visual Regression Detection',
    description:
      'When a competitor changes their pricing page or rewrites a CTA, you need to know immediately — not next quarter. The Curtain Slider shows exactly what changed, and AI-powered exclusion regions filter out cookie banners and chat widgets so you focus on what matters.',
    highlights: [
      { label: 'Curtain Slider', type: 'cyan' },
      { label: 'Exclusion Regions', type: 'amber' },
    ],
    image: null,
    alignment: 'left',
  },
  {
    id: 'keyword-battlefield',
    icon: TrendingUp,
    title: 'The Keyword Landscape',
    description:
      'See exactly where every client ranks against every competitor in their vertical. Win/Loss tallies show momentum at a glance. Overlay GA4 traffic data onto ranking history to connect position changes to actual revenue — the proof your clients need to see.',
    highlights: [
      { label: 'Win/Loss Tally', type: 'cyan' },
      { label: 'GA4 Traffic Overlay', type: 'cyan' },
    ],
    image: null,
    alignment: 'right',
  },
  {
    id: 'discovery-hub',
    icon: Users,
    title: 'Discovery Hub',
    description:
      'New competitors emerge constantly — VC-backed startups, enterprise pivots, and niche disruptors. Discovery Hub uses deep SERP scanning to identify emerging players while they are still on page 10 — so your clients never get blindsided.',
    highlights: [
      { label: 'Market Heatmap', type: 'cyan' },
      { label: 'RSS Radar', type: 'amber' },
    ],
    image: null,
    alignment: 'left',
  },
  {
    id: 'signal-routing',
    icon: MessageSquare,
    title: 'Signal Orchestrator',
    description:
      'Route each client\'s signals to the right team member automatically. SERP drops go to your SEO lead, visual changes to the content team, pricing pivots to the strategist. One signal, one owner, zero context-switching.',
    highlights: [
      { label: 'Slack', type: 'cyan' },
      { label: 'Asana', type: 'cyan' },
      { label: 'Webhooks', type: 'cyan' },
    ],
    image: null,
    alignment: 'right',
  },
];

// ─────────────────────────────────────────────────
// Use Cases / Tabs (Section 5)
// ─────────────────────────────────────────────────

export const useCases = [
  {
    id: 'cmo',
    title: 'Agency Directors',
    subtitle: 'See every client market at a glance.',
    description:
      "Stop relying on weekly standups to learn that a client's competitor launched a pricing war three days ago. Tivion gives you a real-time portfolio view — which accounts are healthy, which need attention, and where competitors are gaining ground. Make the call before the client calls you.",
    benefits: [
      'Multi-client portfolio dashboard',
      'Competitor threat scoring per account',
      'Proactive client communication',
    ],
    image: null,
  },
  {
    id: 'seo',
    title: 'SEO Strategists',
    subtitle: 'Prove ROI with revenue, not just rankings.',
    description:
      "Your client doesn't care that they moved from #8 to #3. They care that it drove 2,400 more visits and $18,000 in pipeline. Tivion overlays GA4 traffic directly onto ranking history so you can show the dollar impact of every optimization — and flag anomalies before they cost revenue.",
    benefits: [
      'Keyword-level revenue attribution',
      'Share of Voice trending',
      'Traffic anomaly detection',
    ],
    image: null,
  },
  {
    id: 'agency',
    title: 'Account Managers',
    subtitle: 'Turn intelligence into client trust.',
    description:
      "When a competitor launches a new pricing tier, your client should hear it from you first — not from their sales team. Tivion's Client Portal gives each account a curated, read-only view of their competitive landscape with your strategic commentary pinned to every signal.",
    benefits: [
      'Branded client portals',
      'Pinned strategic commentary',
      'Automated weekly reports',
    ],
    image: null,
  },
  {
    id: 'product',
    title: 'Content & Dev Teams',
    subtitle: 'See exactly what competitors are building.',
    description:
      "Know the moment a competitor migrates to a headless CMS, launches a new product page cluster, or starts A/B testing their pricing. Visual regression and tech stack detection give your team the evidence to prioritize the right projects for each client.",
    benefits: [
      'Tech stack change detection',
      'Visual regression archive',
      'Competitor content monitoring',
    ],
    image: null,
  },
];

// ─────────────────────────────────────────────────
// Trust Points (Section 6)
// ─────────────────────────────────────────────────

export const trustPoints = [
  {
    title: 'Multi-Client Portfolio Intelligence',
    description:
      'Manage dozens of competitor landscapes from a single grid. Purpose-built for agencies who need cross-client visibility without the overhead.',
  },
  {
    title: 'Always-On Market Monitoring',
    description:
      'Detect pricing pivots, tech-stack migrations, and SERP shifts the moment they happen — so your team can respond before the market catches up.',
  },
  {
    title: 'Secure, Role-Based Access',
    description:
      'Enterprise-grade RBAC, encrypted data, and client portals let you share the right intelligence with the right stakeholders — nothing more.',
  },
];

// ─────────────────────────────────────────────────
// Live Pulse Feed — Pulse Stories (Section 5B)
// ─────────────────────────────────────────────────

export const pulseStories = {
  fintech: {
    label: 'Fintech',
    companies: ['NexusPay', 'Vertex Finance'],
    events: [
      {
        company: 'NexusPay',
        signal: 'Pricing Pivot',
        category: 'visual',
        severity: 'high',
        text: 'Enterprise tier dropped from $499/mo to $299/mo. Undercutting your client by 40%.',
        miniDiff: { before: '$499/mo', after: '$299/mo', label: 'Pricing Page' },
      },
      {
        company: 'Vertex Finance',
        signal: 'SERP Movement',
        category: 'seo',
        severity: 'high',
        text: 'Now ranking #2 for "payment processing API" (was #7). Gained 5 positions in 10 days.',
      },
      {
        company: 'NexusPay',
        signal: 'Content Strategy',
        category: 'strategy',
        severity: 'medium',
        text: '12 new comparison pages targeting "Stripe alternative" and "payment gateway for SaaS".',
      },
      {
        company: 'Vertex Finance',
        signal: 'Visual Change',
        category: 'visual',
        severity: 'low',
        text: 'Homepage hero rewritten — new "Zero Integration Fees" headline above the fold.',
      },
      {
        company: 'NexusPay',
        signal: 'Tech Stack Shift',
        category: 'tech',
        severity: 'medium',
        text: 'Detected: migrated from WordPress to Next.js + Contentful headless CMS.',
      },
    ],
  },
  saas: {
    label: 'SaaS',
    companies: ['CloudFlow', 'StreamlineAI'],
    events: [
      {
        company: 'CloudFlow',
        signal: 'SERP Movement',
        category: 'seo',
        severity: 'high',
        text: 'Jumped from #6 to #2 for "project management software". Gaining fast on your client.',
      },
      {
        company: 'StreamlineAI',
        signal: 'Pricing Pivot',
        category: 'visual',
        severity: 'high',
        text: '"Free Forever" tier launched with 10-user limit. New freemium play.',
        miniDiff: { before: '$29/mo starter', after: 'Free Forever tier', label: 'Pricing Page' },
      },
      {
        company: 'CloudFlow',
        signal: 'Content Strategy',
        category: 'strategy',
        severity: 'medium',
        text: 'New content hub: 15 posts targeting "AI workflow automation" keyword cluster.',
      },
      {
        company: 'StreamlineAI',
        signal: 'Tech Stack Shift',
        category: 'tech',
        severity: 'medium',
        text: 'Added Segment, Amplitude, and Intercom — full product-led growth stack deployed.',
      },
      {
        company: 'CloudFlow',
        signal: 'Visual Change',
        category: 'visual',
        severity: 'low',
        text: 'Product tour redesigned with interactive demo — replacing static screenshots.',
      },
    ],
  },
  ecommerce: {
    label: 'E-Commerce',
    companies: ['SwiftCart', 'MarketEdge'],
    events: [
      {
        company: 'SwiftCart',
        signal: 'Position Shift',
        category: 'seo',
        severity: 'high',
        text: 'Now ranking #1 for "headless commerce platform" (was #5). Major content push.',
      },
      {
        company: 'MarketEdge',
        signal: 'Pricing Pivot',
        category: 'visual',
        severity: 'high',
        text: 'Transaction fees cut from 2.9% to 1.5%. Aggressive enterprise play.',
        miniDiff: { before: '2.9% + $0.30', after: '1.5% flat', label: 'Pricing Page' },
      },
      {
        company: 'SwiftCart',
        signal: 'Content Strategy',
        category: 'strategy',
        severity: 'medium',
        text: 'Published 8 new migration guides targeting "Shopify alternative" keywords.',
      },
      {
        company: 'MarketEdge',
        signal: 'Visual Change',
        category: 'visual',
        severity: 'low',
        text: 'Case study section redesigned — added ROI calculators and video testimonials.',
      },
      {
        company: 'SwiftCart',
        signal: 'Tech Stack Shift',
        category: 'tech',
        severity: 'medium',
        text: 'Added Vercel Edge Functions and Algolia search — performance-focused replatform.',
      },
    ],
  },
};
